import { useCallback, useEffect, useRef, useState } from "react";
import type { Exercise } from "../core/exercises";
import type { RawRecording } from "../core/motion";
import {
  loadRepSensor, profileOf, saveRepSensor, sensorOn, storeSensorRecording, withSensorOn, WORKOUT_PLACEMENT, type RepSensorSettings,
} from "../core/repSensor";
import type { Backend } from "../core/storage";
import { entryExercise, markSensorSet, sensorReps, sensorTimer, updateWorkoutSet, type Workout } from "../core/workout";
import { platform } from "../platform";
import { APP_VERSION } from "../version";
import { liveCount, type LiveCount } from "./liveCount";
import type { MotionSource } from "./motionSource";
import { t } from "./texts";

type Apply = (fn: (w: Workout, now: number) => Workout) => void;

interface Running {
  exKey: string;
  setKey: string;
  exerciseId: string;
  src?: MotionSource;
  count?: LiveCount;
  /** ms since 1970 when the sensor started */
  startedAt: number;
  /** ms since 1970 when the work starts (after the lead-in); follows a pause */
  workStart: number;
  /** a rep set (todo 17) has no duration: its set ends when its timer went away */
  open: boolean;
  /** set when the timer went away while the sensor was still starting */
  gone: boolean;
}

/**
 * The motion sensor in a workout (todo 8): while the timer of a `timed` set runs and "Reps per Sensor zählen" is on
 * for its exercise, the sensor records and counts; the count goes into the timer (no taps then) and so into the row.
 * When the set ends its recording is stored with the count, and the set keeps the recording's id; when the workout
 * ends `settleSensorSets` gives it the corrected reps. A cancelled set's recording is dropped.
 *
 * Lives next to `useWorkout` in the App, so it does not depend on the tab shown. `settings` hold the switches and
 * profiles; `setOn` changes a switch (from a tap: it checks the sensor and asks for the permission where needed).
 */
export function useRepSensor(db: Backend | undefined, workout: Workout | undefined, exercises: Exercise[], apply: Apply) {
  const [settings, setSettings] = useState<RepSensorSettings>();
  const [notice, setNotice] = useState<string>();
  const running = useRef<Running>(undefined);
  const latest = useRef(workout);
  latest.current = workout;

  useEffect(() => {
    if (db) loadRepSensor(db).then(setSettings).catch((e) => console.error("loading the sensor settings failed", e));
  }, [db]);

  /** the sensor's timer is gone: store the set's recording, or drop it when the set was cancelled */
  const end = useCallback(async (r: Running) => {
    r.gone = true;
    const endedAt = Date.now();
    const { src, count } = r;
    if (!src || !count) return;
    const w = latest.current;
    const x = w?.exercises.find((y) => y.key === r.exKey);
    const set = x?.sets.find((s) => s.key === r.setKey);
    let raw: RawRecording;
    try {
      raw = await src.stop();
    } catch (e) {
      count.dispose();
      return console.error("stopping the sensor failed", e);
    }
    if (!set?.done || !x || !db) return count.dispose();
    // the set's window on the recording's clock: from the work start for the time done, or until "Fertig"
    const setStart = Math.max(0, r.workStart - r.startedAt);
    const setEnd = r.open ? Math.max(setStart, endedAt - r.startedAt) : setStart + (set.durationSec ?? 0) * 1000;
    raw.marks.push({ kind: "setStart", t: setStart }, { kind: "setEnd", t: setEnd });
    const live = count.count;
    const counted = count.finish(raw);
    const e = entryExercise(exercises, x);
    try {
      const recordingId = await storeSensorRecording(db, raw, {
        recordedAt: r.startedAt, appVersion: APP_VERSION, placement: WORKOUT_PLACEMENT, durationSec: Math.round((setEnd - setStart) / 1000),
        reps: counted, counted, notes: "", origin: "workout",
        exercise: { id: x.exerciseId, name: e?.name ?? "", equipment: e?.equipment ?? "", measure: e?.measure ?? "reps" },
      });
      apply((v) => {
        let next = markSensorSet(v, r.exKey, r.setKey, { recordingId, counted });
        // the recount over the whole recording found more or less than the live count: unless corrected meanwhile
        const now = next.exercises.find((y) => y.key === r.exKey)?.sets.find((s) => s.key === r.setKey);
        if (counted !== live && now?.reps === live) next = updateWorkoutSet(next, r.exKey, r.setKey, { reps: counted }, e);
        return next;
      });
    } catch (err) {
      setNotice(t.sensor.storeFailed(err instanceof Error ? err.message : String(err)));
    }
  }, [db, exercises, apply]);

  useEffect(() => {
    const tm = workout?.timer;
    const x = tm && workout.exercises.find((y) => y.key === tm.exKey);
    const wanted = tm?.counting && x && settings && sensorOn(settings, x.exerciseId) ? tm : undefined;
    const cur = running.current;
    if (cur && (!wanted || wanted.exKey !== cur.exKey || wanted.setKey !== cur.setKey)) {
      running.current = undefined;
      void end(cur);
    } else if (cur && wanted) {
      cur.workStart = wanted.startedAt;
    }
    if (!wanted || !x || running.current || !settings) return;
    const r: Running = {
      exKey: wanted.exKey, setKey: wanted.setKey, exerciseId: x.exerciseId, startedAt: Date.now(), workStart: wanted.startedAt,
      open: wanted.kind === "count", gone: false,
    };
    running.current = r;
    const profile = profileOf(settings, x.exerciseId, WORKOUT_PLACEMENT);
    void (async () => {
      let src: MotionSource | undefined;
      try {
        src = await platform.motion();
        if (!src) return setNotice(t.sensor.noSensor);
        await src.start();
      } catch (e) {
        return setNotice(t.sensor.startFailed(e instanceof Error ? e.message : String(e)));
      }
      r.startedAt = Date.now();
      if (r.gone) return void src.stop().catch(() => undefined);
      r.src = src;
      r.count = liveCount(src, r.startedAt, profile, (n) => apply((v) => sensorReps(v, r.exKey, r.setKey, n)));
      // the work starts after the lead-in (3-2-1); stowing the phone meanwhile does not count
      r.count.begin(r.workStart);
      apply((v) => sensorTimer(v, r.exKey, r.setKey, true));
    })();
  }, [workout, settings, end, apply]);

  // leaving the app's page (reload) stops the sensors
  useEffect(() => () => {
    const r = running.current;
    r?.count?.dispose();
    void r?.src?.stop().catch(() => undefined);
  }, []);

  /** "Reps per Sensor zählen" for an exercise; switching on checks for the sensor first. False when there is none. */
  const setOn = useCallback(async (exerciseId: string, on: boolean) => {
    if (!db || !settings) return false;
    if (on && !(await platform.motion())) {
      setNotice(t.sensor.noSensor);
      return false;
    }
    const next = withSensorOn(settings, exerciseId, on);
    setSettings(next);
    await saveRepSensor(db, next);
    return true;
  }, [db, settings]);

  return {
    settings,
    setOn,
    notice,
    dismissNotice: () => setNotice(undefined),
    /** reloads the settings (a workout ended and profiles were learned, or the Zähltest learned) */
    reload: () => (db ? loadRepSensor(db).then(setSettings) : Promise.resolve()),
  };
}
